"use client"

import { useState } from "react"
import { Head, useForm, router } from "@inertiajs/react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { toast } from 'sonner'
import {
  Globe,
  User,
  Shield,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Save,
  Eye,
  Server,
} from "lucide-react"
import DashboardLayout from "@/layout/dashboard-layout"

interface Client {
  id: number
  name: string
}

interface Category {
  id: number
  name: string
  color: string
}

interface Plataforma {
  id: number
  name: string
  icon: string
  requires_token: boolean
}

interface Props {
  clients: Client[]
  categories: Category[]
  plataformas: Plataforma[]
  blog?: any
  errors?: string[]
}

export default function NovoBlogPage({ clients, categories, plataformas, blog, errors }: Props) {
  const [isCreating, setIsCreating] = useState(false)
  const { data, setData, processing } = useForm({
    nome: blog?.name || "",
    dominio: blog?.domain || "",
    status: "ativo",
    category: blog?.category_id?.toString() || "",
    plataforma: blog?.plataforma_id?.toString() || "1",
    usuario: blog?.usuario || "",
    senha: blog?.senha || "",
    token: blog?.token || "",
    client_id: blog?.client_id || "",
  })

  const handleInputChange = (field: string, value: string) => {
    setData(field as keyof typeof data, value)
  }

  const handleCreateBlog = async () => {
    setIsCreating(true)
    
    try {
      // Criar objeto com dados do formulário para envio
      const blogData = {
        name: data.nome,
        domain: data.dominio,
        client_id: parseInt(data.client_id),
        active: data.status === "ativo",
        category_id: data.category ? parseInt(data.category) : null,
        plataforma_id: data.plataforma ? parseInt(data.plataforma) : null,
        usuario: data.usuario,
        senha: data.senha,
        token: data.token
      }

      // Enviar via router.post (método direto do Inertia)
      router.post('/dashboard/blogs', blogData, {
        onSuccess: () => {
          toast.success('Blog criado com sucesso!', {
            description: `O blog "${data.nome}" foi adicionado à sua lista.`,
            duration: 4000,
          })
        },
        onError: (errors: any) => {
          console.error('Erro ao criar blog:', errors)
          toast.error('Erro ao criar blog', {
            description: 'Verifique os dados e tente novamente.',
            duration: 4000,
          })
          setIsCreating(false)
        }
      })
    } catch (error) {
      console.error('Erro inesperado:', error)
      toast.error('Erro inesperado', {
        description: 'Tente novamente em alguns instantes.',
        duration: 4000,
      })
      setIsCreating(false)
    }
  }

  const handleBack = () => {
    router.visit("/dashboard/blogs")
  }

  const handleTestConnection = async () => {
    if (!isFormValid) {
      toast.warning('Formulário incompleto', {
        description: 'Preencha todos os campos obrigatórios antes de testar a conexão.',
        duration: 3000,
      })
      return
    }

    toast.promise(
      new Promise((resolve) => {
        // Simular teste de conexão
        setTimeout(() => {
          resolve('success')
        }, 2000)
      }),
      {
        loading: 'Testando conexão...',
        success: 'Conexão estabelecida com sucesso!',
        error: 'Falha na conexão. Verifique as credenciais.',
      }
    )
  }

  const isFormValid = data.nome && data.dominio && data.category && data.usuario && data.senha

  const getCategoryInfo = (categoryId: string) => {
    return categories?.find((c) => c.id.toString() === categoryId)
  }

  const getPlataformaInfo = (plataformaId: string) => {
    return plataformas?.find((p) => p.id.toString() === plataformaId)
  }

  return (
    <DashboardLayout>
      <Head title="Adicionar Novo Blog" />
      
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Adicionar Novo Blog</h1>
            <p className="text-muted-foreground">Cadastre um blog existente para gerenciar postagens no Memoráveis</p>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Formulário Principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Informações Básicas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Informações do Blog
                </CardTitle>
                <CardDescription>Configure as informações básicas do blog existente</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="nome">Nome do Blog *</Label>
                    <Input
                      id="nome"
                      value={data.nome}
                      onChange={(e) => handleInputChange("nome", e.target.value)}
                      placeholder="Ex: Blog de Tecnologia"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dominio">Domínio *</Label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                        https://
                      </span>
                      <Input
                        id="dominio"
                        value={data.dominio}
                        onChange={(e) => handleInputChange("dominio", e.target.value)}
                        placeholder="exemplo.com"
                        className="rounded-l-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Status *</Label>
                    <Select value={data.status} onValueChange={(value) => handleInputChange("status", value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ativo">Ativo</SelectItem>
                        <SelectItem value="inativo">Inativo</SelectItem>
                        <SelectItem value="manutencao">Em Manutenção</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Categoria *</Label>
                    <Select value={data.category} onValueChange={(value) => handleInputChange("category", value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories?.map((category) => (
                          <SelectItem key={category.id} value={category.id.toString()}>
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 rounded-full ${category.color}`}></div>
                              {category.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Cliente *</Label>
                    <Select value={data.client_id?.toString() || ""} onValueChange={(value) => handleInputChange("client_id", value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients?.map((client) => (
                          <SelectItem key={client.id} value={client.id.toString()}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo/Plataforma *</Label>
                    <Select value={data.plataforma} onValueChange={(value) => handleInputChange("plataforma", value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {plataformas?.map((plataforma) => (
                          <SelectItem key={plataforma.id} value={plataforma.id.toString()}>
                            <div className="flex items-center gap-2">
                              <span>{plataforma.icon}</span>
                              {plataforma.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Credenciais de Acesso */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Credenciais de Acesso
                </CardTitle>
                <CardDescription>Configure as credenciais para acessar o blog</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="usuario">Usuário *</Label>
                    <Input
                      id="usuario"
                      value={data.usuario}
                      onChange={(e) => handleInputChange("usuario", e.target.value)}
                      placeholder="Nome de usuário ou email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="senha">Senha *</Label>
                    <Input
                      id="senha"
                      type="password"
                      value={data.senha}
                      onChange={(e) => handleInputChange("senha", e.target.value)}
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="token">Token de API (Opcional)</Label>
                  <Input
                    id="token"
                    value={data.token}
                    onChange={(e) => handleInputChange("token", e.target.value)}
                    placeholder="Token para acesso via API (se disponível)"
                  />
                  <p className="text-xs text-muted-foreground">
                    Token necessário para algumas plataformas como WordPress.com, Medium, etc.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Informações Adicionais */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="h-5 w-5" />
                  Informações da Plataforma
                </CardTitle>
                <CardDescription>Detalhes específicos da plataforma selecionada</CardDescription>
              </CardHeader>
              <CardContent>
                {data.plataforma === "1" && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Suporte completo para WordPress REST API</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Publicação automática de posts</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Gerenciamento de categorias e tags</span>
                    </div>
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Certifique-se de que o WordPress tem as permissões necessárias para API REST.
                      </AlertDescription>
                    </Alert>
                  </div>
                )}

                {data.plataforma === "2" && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Integração com Blogger API v3</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Publicação e edição de posts</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Gerenciamento de labels</span>
                    </div>
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Token de API obrigatório. Obtenha em Google Cloud Console.
                      </AlertDescription>
                    </Alert>
                  </div>
                )}

                {data.plataforma === "3" && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Publicação via Medium API</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Suporte a formatação Markdown</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Tags automáticas</span>
                    </div>
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Token de integração obrigatório. Configure em Medium Settings.
                      </AlertDescription>
                    </Alert>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Painel Lateral - Resumo */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Resumo do Blog</CardTitle>
                <CardDescription>Informações que serão cadastradas</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Nome: {data.nome || "Não informado"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Domínio: {data.dominio || "Não informado"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Status: {data.status}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>
                      Categoria: {getCategoryInfo(data.category)?.name || "Não selecionado"}
                      {getCategoryInfo(data.category) && (
                        <div className={`inline-block w-2 h-2 rounded-full ml-2 ${getCategoryInfo(data.category)?.color}`}></div>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>
                      Plataforma: {getPlataformaInfo(data.plataforma)?.name || "WordPress"}
                      {getPlataformaInfo(data.plataforma) && (
                        <span className="ml-2">{getPlataformaInfo(data.plataforma)?.icon}</span>
                      )}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Ações</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Button className="w-full h-12" onClick={handleCreateBlog} disabled={!isFormValid || isCreating}>
                    {isCreating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Adicionando Blog...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Adicionar Blog
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full h-12 bg-transparent" 
                    disabled={!isFormValid}
                    onClick={handleTestConnection}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    Testar Conexão
                  </Button>
                </div>

                {!isFormValid && (
                  <Alert className="mt-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>Preencha todos os campos obrigatórios (*) para continuar.</AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
